import { skillNodeById } from "../content/skill-tree";
import { formatDeathDetail, formatDeathHeadline } from "../simulation/death-report";
import type { BankedProfile } from "../simulation/persistence";
import type { PilotMeta } from "../simulation/pilot-meta";
import { debriefHeroCopy } from "../simulation/run-debrief";
import { runHookLines } from "../simulation/run-hooks";
import type { RunSummary } from "../simulation/snapshot";

export type RunSummaryHandlers = {
  onRelaunch?: () => void;
};

/** A centered, viewport-level mission report. It never creates a scrolling surface. */
export class RunSummaryModal {
  private readonly root: HTMLElement;
  private readonly returnButton: HTMLButtonElement | null;
  private lastSummaryKey = "";
  private pilotMeta: PilotMeta | null = null;
  private banked: BankedProfile | null = null;
  private reducedMotion = false;

  public constructor(
    host: HTMLElement,
    private readonly handlers: RunSummaryHandlers = {},
  ) {
    this.root = host.ownerDocument.createElement("aside");
    this.root.className = "mission-report-layer";
    this.root.hidden = true;
    this.root.innerHTML = `
      <article class="mission-report" role="dialog" aria-modal="true" aria-labelledby="mission-report-title">
        <div class="mission-report-topline" aria-hidden="true">
          <span>FLIGHT RECORD</span><i></i><span>ZR-77</span>
        </div>
        <main class="mission-report-content" data-content></main>
        <footer class="mission-report-footer">
          <p data-next-step></p>
          <button type="button" class="mission-report-return" data-relaunch>
            <span>Return to carrier</span><kbd>R</kbd>
          </button>
        </footer>
      </article>
    `;
    // Portaling to body makes fixed positioning independent of the canvas shell,
    // its grid row, and any future transformed presentation ancestors.
    host.ownerDocument.body.appendChild(this.root);
    this.returnButton = this.root.querySelector<HTMLButtonElement>("[data-relaunch]");
    this.returnButton?.addEventListener("click", () => this.handlers.onRelaunch?.());
  }

  public sync(
    summary: RunSummary | null,
    pilotMeta?: PilotMeta,
    opts?: { reducedMotion?: boolean; banked?: BankedProfile },
  ): void {
    this.pilotMeta = pilotMeta ?? null;
    this.banked = opts?.banked ?? null;
    this.reducedMotion = Boolean(opts?.reducedMotion);
    if (!summary) {
      this.root.hidden = true;
      document.documentElement.classList.remove("mission-report-open");
      this.root.classList.remove("mission-report-layer--enter");
      this.lastSummaryKey = "";
      return;
    }

    const key = `${summary.outcome}:${summary.score}:${summary.grade}:${summary.totalKills}`;
    document.documentElement.classList.add("mission-report-open");
    this.root.hidden = false;
    if (key !== this.lastSummaryKey) {
      this.lastSummaryKey = key;
      this.render(summary);
      this.playEnter();
      this.returnButton?.focus({ preventScroll: true });
    }
  }

  public dispose(): void {
    document.documentElement.classList.remove("mission-report-open");
    this.root.remove();
  }

  private playEnter(): void {
    this.root.classList.remove("mission-report-layer--enter");
    if (this.reducedMotion) return;
    void this.root.offsetWidth;
    this.root.classList.add("mission-report-layer--enter");
  }

  private render(summary: RunSummary): void {
    const content = this.root.querySelector<HTMLElement>("[data-content]");
    const nextStep = this.root.querySelector<HTMLElement>("[data-next-step]");
    if (content) content.innerHTML = renderReport(summary, this.pilotMeta);

    const hook = this.banked ? runHookLines(this.banked, summary)[0] : null;
    if (nextStep) {
      nextStep.textContent = hook?.text ?? "Your carrier is ready for another sortie.";
      nextStep.dataset.kind = hook?.kind ?? "ready";
    }
  }
}

function renderReport(summary: RunSummary, pilot: PilotMeta | null): string {
  const hero = debriefHeroCopy(summary);
  const isLoss = summary.outcome === "dead";
  const upgrades = summary.takenNodes.slice(0, 3).map((id) => skillNodeById(id)?.name ?? id);
  const upgradeOverflow = Math.max(0, summary.takenNodes.length - upgrades.length);

  return `
    <section class="mission-report-hero" data-tone="${hero.tone}">
      <div class="mission-report-heading">
        <p class="mission-report-kicker">${isLoss ? "Sortie terminated" : "Extraction confirmed"}</p>
        <h2 id="mission-report-title">${escapeHtml(hero.headline)}</h2>
        <p class="mission-report-subtitle">${escapeHtml(reportSubtitle(summary))}</p>
      </div>
      <div class="mission-report-grade" data-grade="${summary.grade}" aria-label="Run grade ${summary.grade}">
        <span>Grade</span><strong>${summary.grade}</strong>
      </div>
      <div class="mission-report-score">
        <span>Final score</span><strong>${summary.score.toLocaleString()}</strong>
      </div>
      <dl class="mission-report-metrics" aria-label="Run totals">
        ${reportMetric("Kills", String(summary.totalKills))}
        ${reportMetric("Flight", formatDuration(summary.simTime))}
        ${reportMetric(isLoss ? "Recovered" : "Banked", String(isLoss ? summary.salvageCollected : summary.salvageBanked))}
      </dl>
    </section>
    <section class="mission-report-details">
      ${renderOutcome(summary)}
      <article class="mission-report-panel mission-report-performance">
        <p class="mission-report-label">Flight performance</p>
        <div class="mission-report-performance-grid">
          <div><span>Peak combo</span><strong>×${(summary.debrief?.peakComboMultiplier ?? 1).toFixed(2)}</strong></div>
          <div><span>Best chain</span><strong>${summary.bestChain}</strong></div>
          <div><span>Best streak</span><strong>${summary.bestStreak}</strong></div>
          <div><span>Run level</span><strong>${summary.level}</strong></div>
        </div>
      </article>
      <article class="mission-report-panel mission-report-build">
        <div class="mission-report-panel-heading">
          <p class="mission-report-label">Recovered flight data</p>
          <span>Pilot rank ${pilot?.rank ?? "—"}</span>
        </div>
        <div class="mission-report-chips">
          ${upgrades.length ? upgrades.map((name) => `<span>${escapeHtml(name)}</span>`).join("") : "<em>No run upgrades installed</em>"}
          ${upgradeOverflow > 0 ? `<span>+${upgradeOverflow}</span>` : ""}
        </div>
      </article>
    </section>
  `;
}

function reportMetric(label: string, value: string): string {
  return `<div><dt>${label}</dt><dd>${value}</dd></div>`;
}

function reportSubtitle(summary: RunSummary): string {
  if (summary.outcome === "dead" && summary.deathReport) {
    return `${formatDeathHeadline(summary.deathReport)} · ${formatDeathDetail(summary.deathReport)}`;
  }
  return `Cargo transfer complete · ${summary.salvageBanked} salvage secured`;
}

function renderOutcome(summary: RunSummary): string {
  if (summary.outcome !== "dead") {
    return `<article class="mission-report-panel mission-report-cause mission-report-cause--safe">
      <p class="mission-report-label">Outcome</p>
      <h3>Carrier pad secured</h3>
      <p>${summary.salvageCollected} salvage recovered. ${summary.salvageBanked} transferred to the carrier bank.</p>
    </article>`;
  }

  const report = summary.deathReport;
  const distance = report
    ? report.distanceM < 1.5
      ? "Point-blank"
      : `${report.distanceM.toFixed(0)} m range`
    : "Range unknown";
  return `<article class="mission-report-panel mission-report-cause">
    <p class="mission-report-label">Final contact</p>
    <h3>${escapeHtml(report?.enemyName ?? "Hull failure")}</h3>
    <p>${escapeHtml(report ? `${report.attackLabel} · ${distance}` : "No source telemetry recovered")}</p>
    <dl class="mission-report-damage" aria-label="Final hit damage">
      ${damageMetric("Shield", report?.shieldAbsorbed ?? 0)}
      ${damageMetric("Armor", report?.armorAbsorbed ?? 0)}
      ${damageMetric("Hull", report?.hullDamage ?? 0)}
    </dl>
  </article>`;
}

function damageMetric(label: string, value: number): string {
  return `<div><dt>${label}</dt><dd>${Math.round(value)}</dd></div>`;
}

function formatDuration(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, "0")}`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
